import React, {useState} from "react";
import {Button, makeStyles} from "@fluentui/react-components";
import {
    ThumbLike24Regular,
    ThumbLike24Filled,
    ThumbDislike24Regular,
    ThumbDislike24Filled
} from "@fluentui/react-icons";
import {IFeedbackData, FeedbackType} from "../../types/feedback";
import {DirectionalHint, TooltipHost} from "@fluentui/react";
import feedbackAPI from "../../services/feedbackAPI";

const useStyles = makeStyles({
    container: {
        display: "flex",
        alignItems: "center",
        gap: "2px"
    },
    button: {
        minWidth: "auto",
        padding: "4px"
    },
    feedbackText: {
        marginLeft: "12px"
    }
});

interface IFeedbackButtonsProps {
    businessUnitId: string;
    promptText: string;
    responseText: string;
    datadogTraceId: string;
}

export const FeedbackButtons: React.FC<IFeedbackButtonsProps> = ({
    businessUnitId,
    promptText,
    responseText,
    datadogTraceId
}) => {
    const styles = useStyles();
    const [selectedFeedback, setSelectedFeedback] = useState<FeedbackType | null>(
        null
    );
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [feedbackId, setFeedbackId] = useState<number | null>(null);

    const handleFeedback = async (feedback: FeedbackType) => {
        const newFeedback = selectedFeedback === feedback ? null : feedback;

        setIsSubmitting(true);

        try {
            if (feedbackId) {
                await feedbackAPI.deleteFeedback(feedbackId);
                setFeedbackId(null);
            }

            // Only submit if a *new* selection was made
            if (newFeedback) {
                const feedbackData: IFeedbackData = {
                    businessUnitId,
                    datadogTraceId,
                    promptText,
                    responseText,
                    feedback: newFeedback
                };

                const result = await feedbackAPI.submitFeedback(feedbackData);

                if (result.id) {
                    setFeedbackId(result.id);
                } else {
                    throw new Error("Feedback submission failed");
                }
            }

            setSelectedFeedback(newFeedback);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className={styles.container}>
            <TooltipHost
                content="This response was helpful"
                directionalHint={DirectionalHint.bottomCenter}
            >
                <Button
                    size="medium"
                    icon={
                        selectedFeedback === FeedbackType.ThumbsUp ? (
                            <ThumbLike24Filled />
                        ) : (
                            <ThumbLike24Regular />
                        )
                    }
                    onClick={() => handleFeedback(FeedbackType.ThumbsUp)}
                    disabled={isSubmitting}
                    className={`${styles.button}`}
                />
            </TooltipHost>

            <TooltipHost
                content="This response was not helpful"
                directionalHint={DirectionalHint.bottomCenter}
            >
                <Button
                    size="medium"
                    icon={
                        selectedFeedback === FeedbackType.ThumbsDown ? (
                            <ThumbDislike24Filled />
                        ) : (
                            <ThumbDislike24Regular />
                        )
                    }
                    onClick={() => handleFeedback(FeedbackType.ThumbsDown)}
                    disabled={isSubmitting}
                    className={`${styles.button}`}
                />
            </TooltipHost>
        </div>
    );
};
